import React, { useId } from 'react';

/**
 * CellDrawing
 *
 * A plain picture of the pouch cell a BatPaC design block sizes: the pouch,
 * its raised electrode stack and the + / − terminals, in true proportion, with
 * capacity, size and mass underneath. Every dimension comes from the block's
 * own constants, parameters and derived values (see
 * `build_cell_design_parameters.py`); nothing here re-derives the geometry.
 * The picture fills its box, so the caption carries the absolute size.
 *
 * Props:
 *   scope – constants, parameter values and derived values merged into one map
 */

// Keys the picture reads. A design block without all of them (the SeaLion
// coating template, say) gets no picture.
const REQUIRED = [
  'cell_length_mm', 'cell_width_mm', 'cell_thickness_mm',
  'electrode_length_mm', 'electrode_width_mm',
  'term_ext_length_mm', 'term_ft_length_mm', 'term_int_length_mm', 'term_ext_width_mm',
  'p2t_gap_mm', 'neg_excess_mm', 'sep_excess_length_mm',
];

export function hasCellGeometry(scope) {
  return !!scope && REQUIRED.every(key => Number.isFinite(scope[key]) && scope[key] > 0);
}

// LiTeCAB palette: the design panel's blues, and foil colours for the terminals.
const INK = '#1a1a2e';
const POUCH = { fill: '#eef2f7', stroke: '#5a6b7d' };
const STACK = { top: '#f7faff', bottom: '#cfe0f3', stroke: '#8fb0d6' };
const AL = { fill: '#d5d9de', stroke: '#80868b' };
const CU = { fill: '#e8b48a', stroke: '#b06a2c' };

const W = 300;
const H = 96;
const SIGN_ROOM = 16; // each side, for the + and − beside the terminals

const round = v => String(Math.round(v));

export function cellCaption(scope) {
  const parts = [];
  if (Number.isFinite(scope.cell_capacity_ah)) parts.push(`${round(scope.cell_capacity_ah)} Ah`);
  parts.push(`${round(scope.cell_length_mm)} × ${round(scope.cell_width_mm)} × ${round(scope.cell_thickness_mm)} mm`);
  if (Number.isFinite(scope.cell_g)) parts.push(`${round(scope.cell_g)} g`);
  return parts.join(' · ');
}

const CellDrawing = ({ scope }) => {
  const uid = useId().replace(/:/g, '');
  if (!hasCellGeometry(scope)) return null;

  const {
    cell_length_mm: CL, cell_width_mm: CW,
    electrode_length_mm: L, electrode_width_mm: w,
    term_ext_length_mm: te, term_ft_length_mm: tf, term_int_length_mm: ti, term_ext_width_mm: tw,
    p2t_gap_mm: gap, neg_excess_mm: negExcess, sep_excess_length_mm: sepExcessL,
  } = scope;

  // Fit the whole cell, terminals included, into the box.
  const tall = Math.max(CW, tw);
  const s = Math.min((W - 2 * SIGN_ROOM) / CL, (H - 8) / tall);
  const ox = (W - CL * s) / 2;          // + terminal tip
  const cy = H / 2;                     // centre line
  const x0 = ox + te * s;               // pouch ends
  const x1 = ox + (CL - te) * s;
  const oy = cy - (CW * s) / 2;
  // The raised stack spans the separator lengthwise and the anode across.
  const ex = ox + (te + tf + ti + gap) * s;
  const stackX = ex - (sepExcessL / 2) * s;
  const stackW = (L + sepExcessL) * s;
  const stackH = (w + negExcess) * s;
  const termH = tw * s;
  const grad = `stack-${uid}`;

  const caption = cellCaption(scope);

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`Pouch cell: ${caption}`}
        style={{ display: 'block', maxHeight: '120px' }}
      >
        <defs>
          <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={STACK.top} />
            <stop offset="1" stopColor={STACK.bottom} />
          </linearGradient>
        </defs>
        <rect x={ox} y={cy - termH / 2} width={te * s + 2} height={termH} rx="1.5" fill={AL.fill} stroke={AL.stroke} />
        <rect x={x1 - 2} y={cy - termH / 2} width={te * s + 2} height={termH} rx="1.5" fill={CU.fill} stroke={CU.stroke} />
        <rect x={x0 + 2} y={oy + 2} width={x1 - x0} height={CW * s} rx="3" fill={INK} opacity="0.08" />
        <rect x={x0} y={oy} width={x1 - x0} height={CW * s} rx="3" fill={POUCH.fill} stroke={POUCH.stroke} />
        <rect x={stackX} y={cy - stackH / 2} width={stackW} height={stackH} rx="2"
          fill={`url(#${grad})`} stroke={STACK.stroke} />
        <text x={ox - 4} y={cy + 4.5} textAnchor="end" fontSize="14" fontWeight="600" fill={INK}>+</text>
        <text x={ox + CL * s + 4} y={cy + 4.5} fontSize="14" fontWeight="600" fill={INK}>−</text>
      </svg>
      <figcaption style={{ marginTop: '6px', fontSize: '12px', color: INK, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
        {caption}
      </figcaption>
    </figure>
  );
};

export default CellDrawing;
